/**
 * Reading a publish's progress off the `tenancy_solution_deployments` rows.
 *
 * These rows are the only server-side record of HOW a publish went that every viewer can
 * see: a solution version has no `FAILED` status (it returns to `ACTIVE` on failure exactly
 * as on success), so before this the frontend inferred failure from a module-level Set that
 * only the publisher's own browser ever wrote to — a coworker was always told the publish
 * succeeded.
 *
 * A row is NOT one attempt: the backend upserts it per `(solutionVersionId, environment)`,
 * so a republish and a retry after a failure reuse it. The attempt is identified by the
 * solution lock's `publishStartedAt` instead (PSTUDIO-4333) — see `buildPublishAttemptId`.
 */

/**
 * The backend's publish lock expires after this long (`LOCK_TIMEOUT_MS` in
 * `acquire-publish-lock.ts`): a PUBLISHING status older than that is a publish the backend
 * itself no longer honours, so it must not keep the Publish button disabled either.
 */
export const PUBLISH_LOCK_TIMEOUT_MS = 15 * 60 * 1000;

/**
 * How far before the lock's start a row's own timestamp may be and still belong to this
 * attempt. Both clocks are the backend's, but not necessarily the same pod's.
 */
const ATTEMPT_CLOCK_SKEW_MS = 5000;

/**
 * How long after the lock is released to keep waiting for the attempt's own row before
 * falling back to the status. The backend marks the row terminal before it releases the
 * lock (`handle-solution-deployed(-fail).ts`), but the two reach this tab on independent
 * live queries, so ACTIVE can arrive first — and without its row, "finished" cannot be told
 * from "failed".
 */
export const PUBLISH_OUTCOME_GRACE_MS = 5000;

/**
 * An ISO date-time with no offset: `2026-10-05T13:45:10.123`. The backend declares
 * `Solution.publishStartedAt` and the deployment rows' times as Prisma `DateTime` without
 * `@db.Timestamptz`, i.e. `timestamp without time zone`, and Hasura was observed on main to
 * serve such a column (`tenancy_solution_deployments.createdAt`) without an offset.
 */
const OFFSETLESS_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/**
 * A backend timestamp in epoch ms, or `NaN` when it is absent or unparsable.
 *
 * `Date.parse` reads an offset-less date-time as LOCAL time, while the backend writes UTC.
 * In a UTC+ zone that put a lock taken seconds ago hours in the past, so the 15-minute
 * expiry fired at once and a healthy publish was reported as stuck; in a UTC− zone a lock
 * read as hours in the future and never expired. Such a string is read as UTC here. A
 * string that carries `Z` or an offset, or is not a date-time at all, is passed to
 * `Date.parse` unchanged.
 */
export const parseBackendTime = (value?: string | null): number => {
  if (!value) return NaN;
  return Date.parse(OFFSETLESS_DATE_TIME.test(value) ? `${value}Z` : value);
};

const parseTime = parseBackendTime;

/** Whether the lock taken at `publishStartedAt` has outlived the backend's own timeout. */
export const isPublishLockExpired = (
  publishStartedAt: string | null | undefined,
  now: number = Date.now()
): boolean => {
  const startedAt = parseTime(publishStartedAt);
  return (
    Number.isFinite(startedAt) && now - startedAt >= PUBLISH_LOCK_TIMEOUT_MS
  );
};

/**
 * How long until the lock taken at `lockStartedAt` expires, for the timer that re-reads it —
 * `0` once it has, `null` when there is no readable start to time from.
 */
export const msUntilPublishLockExpiry = (
  lockStartedAt: string | null | undefined,
  now: number = Date.now()
): number | null => {
  const startedAt = parseTime(lockStartedAt);
  if (!Number.isFinite(startedAt)) return null;
  return Math.max(0, startedAt + PUBLISH_LOCK_TIMEOUT_MS - now);
};

/**
 * The solution's publish status as "is a publish running", with the backend's own lock
 * rules applied — a lock the backend would hand to the next publish is not a running one:
 * one older than the timeout, and one with no start at all, which `acquirePublishLock`
 * treats as free (PSTUDIO-4333).
 */
export const readSolutionPublishStatus = (
  row: { status?: unknown; publishStartedAt?: unknown } | null | undefined,
  now: number = Date.now()
): 'ACTIVE' | 'PUBLISHING' | null => {
  if (!row) return null;
  if (row.status !== 'PUBLISHING') return 'ACTIVE';
  const publishStartedAt =
    typeof row.publishStartedAt === 'string' ? row.publishStartedAt : null;
  if (!Number.isFinite(parseTime(publishStartedAt))) return 'ACTIVE';
  return isPublishLockExpired(publishStartedAt, now) ? 'ACTIVE' : 'PUBLISHING';
};

/** Terminal success — `DEPLOYMENT_STATUS.Finished` on the backend. */
const FINISHED = 'finished';

/** Terminal failure — either bare `failed` or a step-qualified `"<step> - Failed"`. */
const FAILED = 'failed';

export type DeploymentPhase = 'in_progress' | 'succeeded' | 'failed';

/**
 * What `trackPublishProgress` reports: a deployment phase; `idle` when nothing is being
 * published; `settling` while the lock is released but the attempt's row has not arrived
 * yet (see `PUBLISH_OUTCOME_GRACE_MS`); or `stale` when the lock outlived the backend's
 * timeout with the attempt still unfinished — a pipeline that hung and will push nothing
 * more.
 */
export type PublishProgressPhase =
  | DeploymentPhase
  | 'idle'
  | 'settling'
  | 'stale';

/**
 * A deployment row, flattened. Structural rather than the generated type: every Hasura
 * scalar here comes through codegen as `any`, and the helpers below are unit-tested.
 *
 * Note `deployedBy` is the user's **id** — the Hasura table stores the uuid, unlike the
 * remote-schema `SolutionDeployment.deployedBy`, which is an object (see `DeploymentLike`
 * in derive-deployments.ts). Resolve it against `ctx.projectUsers` for a name.
 */
export type DeploymentProgressRow = {
  id: string;
  environment: string;
  status: string;
  deployedBy?: string | null;
  deployedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  isRepublish?: boolean | null;
  isLatest?: boolean | null;
  solutionVersionId?: string | null;
  versionName?: string | null;
  runs?: {
    id: string;
    status?: string | null;
    updatedAt?: string | null;
  }[];
};

/** The subscription payload's shape, before flattening. Scalars arrive as `any`. */
type RawDeploymentRow = {
  id?: unknown;
  environment?: unknown;
  status?: unknown;
  deployedBy?: unknown;
  deployedAt?: unknown;
  createdAt?: unknown;
  updatedAt?: unknown;
  isRepublish?: unknown;
  isLatest?: unknown;
  solutionVersionId?: unknown;
  solution_version?: { name?: unknown } | null;
  solution_deployment_runs?:
    | ({
        id?: unknown;
        status?: unknown;
        updatedAt?: unknown;
      } | null)[]
    | null;
};

const asText = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined;

/**
 * Flattens the subscription rows into `DeploymentProgressRow`, dropping anything without an
 * id, an environment or a status — those three are what every consumer keys on.
 */
export const toDeploymentProgressRows = (
  rows: (RawDeploymentRow | null | undefined)[] | null | undefined
): DeploymentProgressRow[] =>
  (rows ?? []).reduce<DeploymentProgressRow[]>((result, row) => {
    const id = asText(row?.id);
    const environment = asText(row?.environment);
    const status = asText(row?.status);
    if (!id || !environment || !status) return result;

    result.push({
      id,
      environment,
      status,
      deployedBy: asText(row?.deployedBy),
      deployedAt: asText(row?.deployedAt),
      createdAt: asText(row?.createdAt),
      updatedAt: asText(row?.updatedAt),
      isRepublish: Boolean(row?.isRepublish),
      isLatest: Boolean(row?.isLatest),
      solutionVersionId: asText(row?.solutionVersionId),
      versionName: asText(row?.solution_version?.name),
      runs: (row?.solution_deployment_runs ?? []).flatMap((run) => {
        const runId = asText(run?.id);
        return runId
          ? [
              {
                id: runId,
                status: asText(run?.status),
                updatedAt: asText(run?.updatedAt),
              },
            ]
          : [];
      }),
    });

    return result;
  }, []);

/**
 * Whether a publish is still running, and if not, how it ended.
 *
 * Anything that is not one of the two terminal forms is in flight — and in that case the
 * status string itself names the step the deploy is on, which is what `readDeploymentStep`
 * hands to the UI.
 */
export const readDeploymentPhase = (
  status?: string | null
): DeploymentPhase => {
  const value = (status ?? '').trim().toLowerCase();
  if (value === FINISHED) return 'succeeded';
  // `failed`, `Build - Failed`, `Deploy — failed`: the backend qualifies the failure with
  // the step that failed, so match the suffix rather than the whole string.
  if (value === FAILED || value.endsWith(FAILED)) return 'failed';
  return 'in_progress';
};

/**
 * The step a status names, for display: the whole string while running, and the part before
 * the `- Failed` suffix once it failed. `undefined` when the status carries no step (a bare
 * `failed`, or a finished deploy).
 */
export const readDeploymentStep = (
  status?: string | null
): string | undefined => {
  const value = (status ?? '').trim();
  if (!value) return undefined;

  const phase = readDeploymentPhase(value);
  if (phase === 'succeeded') return undefined;
  if (phase === 'in_progress') return value;

  // Strip the trailing separator + `Failed` only. Splitting on the first dash would cut a
  // step name that contains one ("Set-up environment - Failed" -> "Set").
  const step = value.replace(/\s*[-–—]\s*failed$/i, '').trim();
  return !step || step.toLowerCase() === FAILED ? undefined : step;
};

/**
 * When a row last changed. `updatedAt` is the row's own clock and moves with every step, so
 * it orders attempts correctly even before `deployedAt` is set.
 */
export const readDeploymentTime = (row: DeploymentProgressRow): number => {
  const stamp = row.updatedAt || row.deployedAt || row.createdAt;
  // Read exactly as the lock start is: `trackPublishProgress` compares the two, and reading
  // one as UTC and the other as local would shift a row by the zone's offset.
  const time = parseTime(stamp);
  return Number.isNaN(time) ? 0 : time;
};

const newestFirst = (
  a: DeploymentProgressRow,
  b: DeploymentProgressRow
): number => readDeploymentTime(b) - readDeploymentTime(a);

/** Newest-first copy, so callers never depend on the order the subscription happens to send. */
export const sortDeploymentsNewestFirst = (
  rows: DeploymentProgressRow[] | null | undefined
): DeploymentProgressRow[] => [...(rows ?? [])].sort(newestFirst);

/**
 * The most recent deployment per environment — what the Versions environment cards show.
 * Includes running and failed attempts on purpose: the card's job is to report the latest
 * attempt, not only the last successful one (which `deriveDeployedVersions` already gives).
 */
export const pickLatestDeploymentPerEnvironment = (
  rows: DeploymentProgressRow[] | null | undefined
): Record<string, DeploymentProgressRow> => {
  const latest: Record<string, DeploymentProgressRow> = {};

  for (const row of sortDeploymentsNewestFirst(rows)) {
    const environment = (row.environment ?? '').toLowerCase();
    if (environment && !latest[environment]) latest[environment] = row;
  }

  return latest;
};

/** The newest deployment still running, if any — the one the in-progress toast describes. */
export const pickRunningDeployment = (
  rows: DeploymentProgressRow[] | null | undefined
): DeploymentProgressRow | undefined =>
  sortDeploymentsNewestFirst(rows).find(
    (row) => readDeploymentPhase(row.status) === 'in_progress'
  );

/**
 * The newest row that is new, or whose own `updatedAt` moved, since `baseline` was taken —
 * the row a publish started after that snapshot wrote. Backend timestamps compared with the
 * backend's own, so no client clock is involved (see `beginLocalPublishAttempt`).
 */
export const pickDeploymentChangedSince = (
  rows: DeploymentProgressRow[] | null | undefined,
  baseline: ReadonlyMap<string, string | null>
): DeploymentProgressRow | undefined =>
  sortDeploymentsNewestFirst(rows).find(
    (row) =>
      !baseline.has(row.id) || baseline.get(row.id) !== (row.updatedAt ?? null)
  );

/** The newest deployment of one specific solution version, whatever its phase. */
export const pickDeploymentForVersion = (
  rows: DeploymentProgressRow[] | null | undefined,
  solutionVersionId?: string | null
): DeploymentProgressRow | undefined =>
  solutionVersionId
    ? sortDeploymentsNewestFirst(rows).find(
        (row) => row.solutionVersionId === solutionVersionId
      )
    : undefined;

/**
 * The deploy log as text, for the read-only editor the Versions page opens on a failed
 * publish.
 *
 * `structuredLogs` is preferred when present — it groups the output by step and names the one
 * that failed, which is the whole question being asked — with `rawLogs` as the fallback.
 * Returns undefined when there is nothing to show: the backend returns no logs at all when a
 * publish failed before its Gitea run started, which is exactly the case on QA1 today, so
 * the caller must say so rather than open an empty editor.
 */
export const buildDeploymentLogText = (
  logs?: {
    rawLogs?: unknown;
    structuredLogs?:
      | ({ name?: unknown; status?: unknown; lines?: unknown } | null)[]
      | null;
  } | null
): string | undefined => {
  const steps = (logs?.structuredLogs ?? []).flatMap((step) => {
    const name = asText(step?.name) ?? '';
    const status = asText(step?.status);
    const lines = Array.isArray(step?.lines)
      ? step.lines.map((line) => String(line))
      : [];
    if (!name && !lines.length) return [];
    return [[`── ${name}${status ? ` [${status}]` : ''}`, ...lines].join('\n')];
  });

  if (steps.length) return steps.join('\n\n');

  const raw = asText(logs?.rawLogs);
  return raw?.trim() ? raw : undefined;
};

/**
 * Who published, as a display name.
 *
 * The row stores only the user id, so the name has to come from the project's user list
 * (structural on purpose — `ProjectUser` lives in the XState types, which import this
 * module). `undefined` when the id is unknown there, e.g. a publish by someone since removed
 * from the project; callers must have wording that works without a name.
 */
export const readDeploymentAuthorName = (
  userId: string | null | undefined,
  users:
    | ({
        id: string;
        firstName?: string | null;
        lastName?: string | null;
      } | null)[]
    | null
    | undefined
): string | undefined => {
  if (!userId) return undefined;
  const user = (users ?? []).find((candidate) => candidate?.id === userId);
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  return name || undefined;
};

/**
 * Which publish attempt to describe, and how it is going. Pure — the caller keeps
 * `trackedDeploymentId` between calls and feeds back what this returns.
 *
 * Following one attempt by id, rather than re-picking a row every time, is what makes a
 * republish work: a republish deploys an OLD version, so its row does not belong to the draft
 * version the caller is subscribed to, and re-picking by version would lose it halfway.
 *
 * A row is only adopted while the version status says a publish is running. That is
 * deliberate: a row left non-terminal by a backend that died would otherwise read as "still
 * publishing" forever and hold every viewer in read-only mode. Tracking stops as soon as the
 * attempt reaches a terminal status.
 *
 * With no row to follow, the phase comes from the version status alone — the pre-deployment
 * -rows behaviour, which is also what a solution whose rows are unreadable falls back to.
 *
 * PSTUDIO-4333 scopes all of this to ONE attempt, identified by `publishStartedAt` — the
 * start of the lock this tab saw it take, which the caller keeps after the lock is released:
 *
 * - only a row written at or after it is adopted, so an older row the backend left
 *   non-terminal is not mistaken for this attempt and followed forever. Such a row is
 *   adopted in ANY phase, and whatever the status: a fast failure can reach its row before
 *   the tab ever sees it running, or after the lock is already released;
 * - once the lock is released (`releasedAt`) with no row for the attempt yet, the phase is
 *   `settling` for `PUBLISH_OUTCOME_GRACE_MS` before it falls back to the status;
 * - once the live lock (`lockStartedAt`) outlives the backend's timeout with the attempt
 *   unfinished, the phase is `stale` and tracking stops. A hung pipeline pushes no further
 *   payload, so the caller re-runs this on a timer (`now`) — waiting for a row never ends.
 *
 * Without a `publishStartedAt` it behaves as before: a running row is adopted only while the
 * status says PUBLISHING.
 */
export const trackPublishProgress = ({
  trackedDeploymentId,
  versionStatus,
  solutionVersionId,
  deployments,
  publishStartedAt,
  lockStartedAt,
  releasedAt,
  now = Date.now(),
}: {
  trackedDeploymentId?: string | null;
  versionStatus?: string | null;
  solutionVersionId?: string | null;
  deployments?: DeploymentProgressRow[] | null;
  /** When the attempt's lock was taken — kept by the caller after the lock is released. */
  publishStartedAt?: string | null;
  /** The live lock's start, while the solution still says PUBLISHING. */
  lockStartedAt?: string | null;
  /** When this tab saw the attempt's lock released. */
  releasedAt?: number | null;
  now?: number;
}): {
  phase: PublishProgressPhase;
  deployment?: DeploymentProgressRow;
  step?: string;
  trackedDeploymentId?: string;
} => {
  const rows = deployments ?? [];
  const isPublishing = versionStatus === 'PUBLISHING';
  const lockExpired = isPublishLockExpired(lockStartedAt, now);

  const startedAt = parseTime(publishStartedAt);
  const attemptKnown = Number.isFinite(startedAt);
  // The skew allowance is for a row this attempt is still writing. A row already terminal
  // before the lock was taken is the previous attempt's — adopted, a quick retry would
  // announce the old outcome and then swallow its own under the same attempt id.
  const attemptRows = attemptKnown
    ? rows.filter(
        (row) =>
          readDeploymentTime(row) >=
          (readDeploymentPhase(row.status) === 'in_progress'
            ? startedAt - ATTEMPT_CLOCK_SKEW_MS
            : startedAt)
      )
    : [];

  const adopted = attemptKnown
    ? (pickDeploymentForVersion(attemptRows, solutionVersionId) ??
      sortDeploymentsNewestFirst(attemptRows)[0])
    : isPublishing
      ? (pickDeploymentForVersion(rows, solutionVersionId) ??
        pickRunningDeployment(rows))
      : undefined;

  const tracked =
    (trackedDeploymentId
      ? rows.find((row) => row.id === trackedDeploymentId)
      : undefined) ?? adopted;

  if (tracked) {
    const phase = readDeploymentPhase(tracked.status);
    if (phase === 'in_progress' && lockExpired) {
      return {
        phase: 'stale',
        deployment: tracked,
        step: readDeploymentStep(tracked.status),
      };
    }
    return {
      phase,
      deployment: tracked,
      step: readDeploymentStep(tracked.status),
      trackedDeploymentId: phase === 'in_progress' ? tracked.id : undefined,
    };
  }

  if (lockExpired) return { phase: 'stale' };
  if (isPublishing) return { phase: 'in_progress' };

  if (
    attemptKnown &&
    releasedAt != null &&
    now - releasedAt < PUBLISH_OUTCOME_GRACE_MS
  ) {
    return { phase: 'settling' };
  }

  return { phase: 'idle' };
};
